import Vue from 'vue';
import VueRouter, { RouteConfig } from 'vue-router';
import { AppModule } from '@/store/modules/app';
import { Languages } from '@/types/languagesType';
import DefaultTemplate from '@/layouts/DefaultTemplate.vue';
import CabinetTemplate from '@/layouts/CabinetTemplate.vue';
import Welcome from '@/views/WelcomeView.vue';
import { UserModule } from '@/store/modules/user';
import { ThemesModule } from '@/store/modules/themes';
import { getByDomain } from '@/config/sites';
import { UserType } from '@/types/ServerToken';

Vue.use(VueRouter);

// maps the `cabinet` query param to the internal user type
const CABINET_TYPE_MAP: Record<string, number> = {
  applicant: UserType.applicant,
  consultant: UserType.consultant,
  company: UserType.company,
};

const routes: Array<RouteConfig> = [
  // TODO тимчасова заглушка: гейт демо-логіну для окремих доменів (sites.ts -> accessGate)
  {
    path: '/access-gate',
    name: 'AccessGate',
    component: () => import('../views/AccessGateView.vue'),
  },
  {
    path: '',
    component: DefaultTemplate,
    children: [
      {
        path: '/',
        name: 'Welcome',
        component: Welcome,
      },
      {
        path: '/jobs',
        name: 'Jobs',
        component: () => import('../views/HomeView.vue'),
      },
      {
        path: '/workers',
        name: 'Workers',
        component: () => import('../views/SkilledView.vue'),
      },
      {
        path: '/company',
        name: 'Company',
        component: () => import('../views/CompanyView.vue'),
      },
      {
        path: '/map',
        name: 'Map',
        component: () => import('../views/MapView.vue'),
      },
      {
        path: '/impressum',
        name: 'Impressum',
        component: () => import('../views/ImpressumView.vue'),
      },
      {
        path: '/contact',
        name: 'Contact',
        component: () => import('../views/ContactView.vue'),
      },
      {
        path: '/iframe-workers',
        name: 'WorkersIframe',
        component: () => import('../views/Iframe/WorkersIframeView.vue'),
      },
    ],
  },
  {
    path: '',
    component: CabinetTemplate,
    children: [
      {
        path: '/cabinet',
        name: 'Cabinet',
        component: () => import('../views/Profile/CabinetView.vue'),
      },
    ],
  },
];

const router = new VueRouter({
  mode: 'history',
  base: process.env.BASE_URL,
  routes,
});

router.beforeEach((to, from, next) => {
  const ThemeDetection = getByDomain();

  if (ThemeDetection) {
    AppModule.ActionSid(ThemeDetection.sid);

    // add theme
    ThemesModule.SetTheme(ThemeDetection);
  }

  // language query redirect
  if (to.query.lang && Object.values(Languages).includes(to.query.lang as Languages)) {
    AppModule.ActionLanguage(String(to.query.lang) as Languages);
  } else {
    if (ThemeDetection && ThemeDetection.language) {
      AppModule.ActionLanguage(ThemeDetection.language);
    }
    // @ts-ignore
    next({ ...to, query: { lang: AppModule.language, auth: to.query.auth || undefined }, replace: true });
    return;
  }

  // TODO тимчасова заглушка: гейт демо-логіну для окремих доменів (sites.ts -> accessGate)
  if (ThemeDetection?.accessGate) {
    const hasGateAccess = UserModule.gateAccess === 'GateAccess' && UserModule.gateBanned !== 'GateBanned';
    if (!hasGateAccess && to.name !== 'AccessGate') {
      next({ name: 'AccessGate', query: { lang: AppModule.language } });
      return;
    }
    if (hasGateAccess && to.name === 'AccessGate') {
      next({ path: '/', query: { lang: AppModule.language } });
      return;
    }
  }

  // auto-login via deep link: /cabinet?access_token={token}&cabinet=applicant|consultant|company
  if (to.path === '/cabinet' && to.query.access_token && to.query.cabinet) {
    const cabinet = String(to.query.cabinet);
    const type = CABINET_TYPE_MAP[cabinet];
    if (type !== undefined) {
      UserModule.UpdateServerTokenParams({
        params: {
          access_token: String(to.query.access_token),
          refresh_token: to.query.refresh_token ? String(to.query.refresh_token) : '',
          exp: to.query.expires ? String(to.query.expires) : '',
        },
        type,
      });
      // strip the token from the URL so it is not exposed in the address bar / history
      const {
        access_token, cabinet: _cabinet, refresh_token, expires, ...restQuery
      } = to.query;
      next({ path: '/cabinet', query: { ...restQuery, lang: AppModule.language }, replace: true });
      return;
    }
  }

  if (!UserModule.accessTokenApplicant && !UserModule.accessTokenConsultant && !UserModule.accessTokenCompany) {
    if (to.path === '/cabinet') {
      next({ path: '/' });
    }
  }

  if (to.query.iframe) {
    AppModule.SetIsIframe(true);
  }

  if (to.query.sid) {
    AppModule.ActionLanguageSid(String(to.query.sid));
  }
  next();
});

export default router;
